import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { clearApiCache, setAuthToken, setUnauthorizedHandler } from "../api";

const AuthContext = createContext(null);
const TOKEN_KEY = "sgp_access_token";
const DEVICE_KEY = "sgp_device_id";
const COMPANY_KEY = "sgp_company_id";
const FY_KEY = "sgp_financial_year";
const WORKSPACE_KEY = "sgp_workspace";
const IDLE_LIMIT_MS = 30 * 60 * 1000; // must match backend IDLE_TIMEOUT_MINUTES
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspaceState] = useState(() => {
    try {
      const raw = sessionStorage.getItem(WORKSPACE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const idleTimer = useRef(null);
  const lastActivity = useRef(Date.now());
  const lastRefresh = useRef(Date.now());
  const refreshing = useRef(false);
  const quietRefreshTimer = useRef(null);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    // Dashboard snapshots can contain financial/operational data. Never let a
    // cached snapshot survive a user switch in the same Windows/browser session.
    for (const key of Object.keys(sessionStorage)) {
      if (key === "sgp_dashboard_snapshot" || key.startsWith("sgp_dashboard_snapshot:")) {
        sessionStorage.removeItem(key);
      }
    }
    sessionStorage.removeItem(COMPANY_KEY);
    sessionStorage.removeItem(FY_KEY);
    sessionStorage.removeItem(WORKSPACE_KEY);
    clearApiCache();
    setAuthToken(null);
    setUser(null);
    setWorkspaceState(null);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (quietRefreshTimer.current) clearTimeout(quietRefreshTimer.current);
  }, []);

  const applySession = useCallback((token, userData) => {
    // A successful login may follow another user's expired/closed session.
    // Purge any legacy dashboard cache before the new identity is applied.
    for (const key of Object.keys(sessionStorage)) {
      if (key === "sgp_dashboard_snapshot" || key.startsWith("sgp_dashboard_snapshot:")) {
        sessionStorage.removeItem(key);
      }
    }
    sessionStorage.removeItem(COMPANY_KEY);
    sessionStorage.removeItem(FY_KEY);
    sessionStorage.removeItem(WORKSPACE_KEY);
    setWorkspaceState(null);
    clearApiCache();
    sessionStorage.setItem(TOKEN_KEY, token);
    setAuthToken(token);
    lastActivity.current = Date.now();
    lastRefresh.current = Date.now();
    setUser(userData);
  }, []);

  const setWorkspace = useCallback((value) => {
    if (!value?.company?.id || !value?.financial_year) return;
    const next = {
      company: value.company,
      financial_year: value.financial_year,
    };
    sessionStorage.setItem(COMPANY_KEY, next.company.id);
    sessionStorage.setItem(FY_KEY, next.financial_year);
    sessionStorage.setItem(WORKSPACE_KEY, JSON.stringify(next));
    clearApiCache();
    for (const key of Object.keys(sessionStorage)) {
      if (key === "sgp_dashboard_snapshot" || key.startsWith("sgp_dashboard_snapshot:")) {
        sessionStorage.removeItem(key);
      }
    }
    setWorkspaceState(next);
  }, []);

  const clearWorkspace = useCallback(() => {
    sessionStorage.removeItem(COMPANY_KEY);
    sessionStorage.removeItem(FY_KEY);
    sessionStorage.removeItem(WORKSPACE_KEY);
    clearApiCache();
    setWorkspaceState(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => clearSession());
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    setAuthToken(token);
    api
      .post("/auth/refresh")
      .then(({ data }) => {
        sessionStorage.setItem(TOKEN_KEY, data.access_token);
        setAuthToken(data.access_token);
        lastActivity.current = Date.now();
        lastRefresh.current = Date.now();
        return api.get("/auth/me");
      })
      .then(async (r) => {
        setUser(r.data);
        const company = sessionStorage.getItem(COMPANY_KEY);
        const financialYear = sessionStorage.getItem(FY_KEY);
        if (company && financialYear) {
          try {
            await api.get("/workspace/validate");
          } catch {
            clearWorkspace();
          }
        }
      })
      .catch(() => clearSession())
      .finally(() => setLoading(false));
  }, [clearSession, clearWorkspace]);

  const refreshSession = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    try {
      const { data } = await api.post("/auth/refresh");
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      setAuthToken(data.access_token);
      lastRefresh.current = Date.now();
    } catch {
      // The response interceptor clears revoked or expired sessions.
    } finally {
      refreshing.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const activity = () => {
      const current = Date.now();
      if (current - lastActivity.current >= IDLE_LIMIT_MS) {
        clearSession();
        return;
      }
      lastActivity.current = current;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(clearSession, IDLE_LIMIT_MS);
      if (current - lastRefresh.current >= REFRESH_INTERVAL_MS) refreshSession();
      if (quietRefreshTimer.current) clearTimeout(quietRefreshTimer.current);
      quietRefreshTimer.current = setTimeout(refreshSession, 30 * 1000);
    };
    idleTimer.current = setTimeout(clearSession, IDLE_LIMIT_MS);
    const events = ["mousedown", "keydown", "scroll", "touchstart", "pointermove"];
    events.forEach((event) => window.addEventListener(event, activity, { passive: true }));
    const onVisible = () => { if (document.visibilityState === "visible") activity(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      events.forEach((event) => window.removeEventListener(event, activity));
      document.removeEventListener("visibilitychange", onVisible);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (quietRefreshTimer.current) clearTimeout(quietRefreshTimer.current);
    };
  }, [user, clearSession, refreshSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password, device_id: getDeviceId() });
    if (data.status === "ok") {
      applySession(data.access_token, data.user);
    }
    return data;
  };

  const verifyTwoFactor = async (tempToken, code, trustDevice) => {
    const { data } = await api.post("/auth/verify-2fa", {
      temp_token: tempToken,
      code,
      trust_device: trustDevice,
      device_id: getDeviceId(),
      device_name: navigator.platform || "This computer",
    });
    applySession(data.access_token, data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore — clearing local session matters more than server ack
    }
    clearSession();
  };

  const completeMicrosoftLogin = async (token) => {
    setAuthToken(token);
    const { data } = await api.get("/auth/me");
    applySession(token, data);
    return data;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, workspace, setWorkspace, clearWorkspace,
      login, verifyTwoFactor, logout, completeMicrosoftLogin, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function can(user, module, level = "view") {
  if (!user) return false;
  const LEVELS = { none: 0, view: 1, qty: 1, add: 1, print: 1, full: 2 };
  const got = LEVELS[user.permissions?.[module] || "none"] ?? 0;
  const need = LEVELS[level] ?? 1;
  return got >= need;
}
